/**
 * Live operations event stream — wraps EventSource with auto-reconnect.
 *
 * Usage:
 *   useLiveEvents(['incident.created', 'incident.closed', ...], (event) => {
 *     // re-fetch local state
 *   });
 *
 * Pass an empty array to subscribe to ALL event types (callback gets every frame).
 *
 * Authentication: relies on the httpOnly access_token cookie set by /api/auth/verify-otp.
 * EventSource with withCredentials:true forwards the cookie automatically.
 */
import { useEffect, useRef } from 'react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const STREAM_URL = `${BASE_URL}/api/events/stream`;

export function useLiveEvents(eventTypes, onEvent) {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    let es;
    let retryMs = 1000;
    let cancelled = false;
    let retryTimer;

    const wantsAll = !eventTypes || eventTypes.length === 0;
    const wantSet = new Set(eventTypes || []);

    const connect = () => {
      if (cancelled) return;
      es = new EventSource(STREAM_URL, { withCredentials: true });

      const handle = (e) => {
        try {
          const parsed = JSON.parse(e.data);
          if (wantsAll || wantSet.has(parsed.type)) {
            cbRef.current?.(parsed);
          }
        } catch {
          /* ignore malformed frame */
        }
      };

      // Server uses `event: <type>` so each type is a named event. Register listeners
      // for each requested type. Also keep a generic 'message' listener for any
      // server-default frames.
      if (wantsAll) {
        // EventSource only fires the named handler — generic 'message' covers
        // frames sent without an event: line. We get the type from the payload.
        es.addEventListener('message', handle);
      }
      (eventTypes || []).forEach((t) => es.addEventListener(t, handle));

      es.addEventListener('open', () => { retryMs = 1000; });
      es.addEventListener('error', () => {
        es.close();
        if (cancelled) return;
        retryTimer = setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, 15000);
      });
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      if (es) es.close();
    };
    // We intentionally compare event types by reference / length so callers can
    // pass an inline array as long as it's stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(eventTypes)]);
}
